import React, { useState } from 'react';
import {
	Box,
	Button,
	Flex,
	FormControl,
	Input,
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { equals } from 'ramda';
import { useUserEditorProvider } from '../../../providers/UserEditor/userEditorProvider';
import { t } from 'i18next';
import { useUserProvider } from '../../../providers/User/userProvider';
import { Color } from 'src/theme/color';

export interface ISocials {
	discord: string;
	facebook: string;
	telegram: string;
	twitch: string;
	youtube: string;
	twitter: string;
}

export const EditSocials = () => {
	const { data: userData } = useUserProvider();
	const editorUserData = useUserEditorProvider();

	const { isOpen, onOpen, onClose } = useDisclosure();

	const [isLoading, setIsLoading] = useState(false);

	const validationSchema = Yup.object().shape({
		discord: Yup.string().max(50, t('default.toLongText')).url(t('default.wrongUrl')).nullable(),
		facebook: Yup.string().max(50, t('default.toLongText')).url(t('default.wrongUrl')).nullable(),
		telegram: Yup.string().max(50, t('default.toLongText')).url(t('default.wrongUrl')).nullable(),
		twitch: Yup.string().max(50, t('default.toLongText')).url(t('default.wrongUrl')).nullable(),
		youtube: Yup.string().max(50, t('default.toLongText')).url(t('default.wrongUrl')).nullable(),
		twitter: Yup.string().max(50, t('default.toLongText')).url(t('default.wrongUrl')).nullable(),
	});

	const onOpenSocials = () => {
		onOpen();
	};

	const socialBody = ({ props, id, isLoading }: { id: string; isLoading: boolean; props: any }) => (
		<Field
			key={id}
			name={id}
		>
			{({ field, form }: any) => (
				<FormControl>
					<Box mb="23px">
						<Text
							mb="11px"
							color="white"
							fontSize="14px"
							fontWeight="600"
							textTransform="capitalize"
						>
							{id}
						</Text>
						<Input
							_focus={{ boxShadow: 'none' }}
							borderColor={Color.BACKGROUND_TERTIARY}
							color="white"
							id={id}
							disabled={isLoading}
							placeholder={id}
							value={field.value}
							onChange={props.handleChange}
							border={`1px solid ${!form.errors[id] ? 'rgb(78, 79, 82)' : `${Color.ACTION} !important`}`}
						/>
						<Flex
							pos="absolute"
							right="0"
							mt="5px"
							color="action"
							fontSize="12px"
							fontWeight="600"
						>
							{form.errors[id]}
						</Flex>
					</Box>
				</FormControl>
			)}
		</Field>
	);

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
			>
				<ModalOverlay />
				<ModalContent
					bg={Color.BACKGROUND_LIGHT}
					border={`1px solid ${Color.BACKGROUND_TERTIARY}`}
					m="0 20px"
					color="black"
					alignSelf="center"
				>
					<ModalHeader
						display="flex"
						justifyContent="space-between"
						alignItems="center"
						color="white"
					>
						<Text
							fontWeight="600"
							fontSize="20px"
						>
							{t('user.socialMedia').toString()}
						</Text>
						<Flex
							cursor="pointer"
							justifyContent="center"
							alignItems="center"
							maxW="20px"
							maxH="20px"
							onClick={onClose}
							border="1px solid white"
							borderRadius="50%"
							p="5px"
						>
							<CloseIcon
								w="7px"
								h="7px"
							/>
						</Flex>
					</ModalHeader>
					<ModalBody
						display="flex"
						flexDir="column"
					>
						<Formik
							initialValues={editorUserData.socials ?? userData.socials}
							validationSchema={validationSchema}
							onSubmit={async (values: any, actions: any) => {
								actions.setSubmitting(false);
								editorUserData.setSocials(values);
								onClose();
							}}
						>
							{props => (
								<Form>
									<Flex
										alignItems="center"
										flexDir="column"
									>
										{Object.keys(userData.socials).map(key =>
											socialBody({
												isLoading,
												id: key,
												props,
											})
										)}
										<Button
											alignSelf="flex-end"
											m="25px 0"
											colorScheme={Color.ACTION}
											disabled={
												!props.isValid ||
												props.isSubmitting ||
												equals(editorUserData.socials ?? userData.socials, props.values) ||
												isLoading
											}
											isLoading={props.isSubmitting || isLoading}
											type="submit"
										>
											{t('default.close').toString()}
										</Button>
									</Flex>
								</Form>
							)}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
			<Flex alignItems="center">
				<Flex
					onClick={onOpenSocials}
					cursor="pointer"
					bg={`${Color.BACKGROUND_LIGHT}80`}
					fontSize={{ base: '8px', hashup: '12px' }}
					p={{ base: '2px 4px', hashup: '4px 8px' }}
					borderRadius="5px"
					fontWeight="400"
					border={`1px solid ${Color.GRAY_BORDER_LIGHT}`}
					borderColor="white"
					justifyContent="center"
					alignItems="center"
				>
					{t('default.edit').toString()}
				</Flex>
			</Flex>
		</>
	);
};
